<template>
  <section class="fg-confirm">
    <!-- État vide (pas de booking en mémoire) -->
    <div v-if="!store.booking" class="fg-confirm__empty">
      <div class="fg-confirm__empty-icon">
        <Icon name="alert" :size="32" />
      </div>
      <h2 class="fg-confirm__empty-title">{{ t('summary.missingBooking') }}</h2>
      <button type="button" class="fg-btn fg-btn--primary" @click="newBooking">
        {{ t('summary.newBooking') }}
      </button>
    </div>

    <template v-else>
      <!-- Header : même grammaire que toutes les étapes du funnel
           (back + titres + stepper de progression). -->
      <header class="fg-page__header">
        <BackButton :disabled="redirecting" @click="goBack" />
        <div class="fg-page__titles">
          <h1 class="fg-page__title">{{ t('summary.title') }}</h1>
          <p class="fg-page__subtitle">{{ t('summary.subtitle') }}</p>
        </div>
        <Stepper current="summary" />
      </header>

      <!-- Layout : détails (gauche) + récap prix/CTA sticky (droite) sur grand
           écran ; pile unique sur mobile. -->
      <div class="fg-confirm__stack">
        <div class="fg-confirm__main">
        <!-- ══ 1. TRAJET ══════════════════════════════════════════ -->
        <section v-if="outwardAv" class="fg-card">
          <header class="fg-card__head">
            <div class="fg-card__icon"><Icon name="ship" :size="16" /></div>
            <h2 class="fg-card__title">{{ t('summary.outward') }}</h2>
          </header>
          <div class="fg-card__body">
            <div class="fg-trip">
              <span class="fg-trip__port fg-trip__port--from">{{ formatPort(outwardAv.depart_name) }}</span>
              <span class="fg-trip__line">
                <span class="fg-trip__dot fg-trip__dot--from" />
                <span class="fg-trip__bar" />
                <Icon name="arrow-right" :size="14" class="fg-trip__arrow" />
                <span class="fg-trip__bar" />
                <span class="fg-trip__dot fg-trip__dot--to" />
              </span>
              <span class="fg-trip__port fg-trip__port--to">{{ formatPort(outwardAv.arrive_name) }}</span>
            </div>
            <div class="fg-trip__schedule">
              <div class="fg-trip__schedule-col">
                <span class="fg-trip__schedule-label">{{ t('summary.outward') }}</span>
                <span class="fg-trip__schedule-value">
                  {{ formatDateHuman(outwardAv.depart_at) }} · {{ formatTime(outwardAv.depart_at, locale) }}
                </span>
              </div>
              <div v-if="outwardDuration" class="fg-trip__schedule-col">
                <span class="fg-trip__schedule-label">{{ t('summary.duration') }}</span>
                <span class="fg-trip__schedule-value">{{ outwardDuration }}</span>
              </div>
            </div>
            <div class="fg-trip__meta">
              <img v-if="outwardAv.company?.logo" :src="outwardAv.company.logo" :alt="outwardAv.company.name || ''" class="fg-trip__logo" />
              <span>{{ humanizeLabel(outwardAv.company?.name) }}</span>
              <span v-if="outwardAv.ship_name" class="fg-trip__sep">·</span>
              <span v-if="outwardAv.ship_name">{{ humanizeLabel(outwardAv.ship_name) }}</span>
            </div>
          </div>
        </section>

        <!-- Trajet retour (si A/R) -->
        <section v-if="returnAv" class="fg-card">
          <header class="fg-card__head">
            <div class="fg-card__icon"><Icon name="ship" :size="16" /></div>
            <h2 class="fg-card__title">{{ t('summary.return') }}</h2>
          </header>
          <div class="fg-card__body">
            <div class="fg-trip">
              <span class="fg-trip__port fg-trip__port--from">{{ formatPort(returnAv.depart_name) }}</span>
              <span class="fg-trip__line">
                <span class="fg-trip__dot fg-trip__dot--from" />
                <span class="fg-trip__bar" />
                <Icon name="arrow-right" :size="14" class="fg-trip__arrow" />
                <span class="fg-trip__bar" />
                <span class="fg-trip__dot fg-trip__dot--to" />
              </span>
              <span class="fg-trip__port fg-trip__port--to">{{ formatPort(returnAv.arrive_name) }}</span>
            </div>
            <div class="fg-trip__schedule">
              <div class="fg-trip__schedule-col">
                <span class="fg-trip__schedule-label">{{ t('summary.return') }}</span>
                <span class="fg-trip__schedule-value">
                  {{ formatDateHuman(returnAv.depart_at) }} · {{ formatTime(returnAv.depart_at, locale) }}
                </span>
              </div>
              <div v-if="returnDuration" class="fg-trip__schedule-col">
                <span class="fg-trip__schedule-label">{{ t('summary.duration') }}</span>
                <span class="fg-trip__schedule-value">{{ returnDuration }}</span>
              </div>
            </div>
            <div class="fg-trip__meta">
              <img v-if="returnAv.company?.logo" :src="returnAv.company.logo" :alt="returnAv.company.name || ''" class="fg-trip__logo" />
              <span>{{ humanizeLabel(returnAv.company?.name) }}</span>
              <span v-if="returnAv.ship_name" class="fg-trip__sep">·</span>
              <span v-if="returnAv.ship_name">{{ humanizeLabel(returnAv.ship_name) }}</span>
            </div>
          </div>
        </section>

        <!-- ══ 2. PASSAGERS + INSTALLATION (fusionnés par groupe) ═ -->
        <section class="fg-card">
          <header class="fg-card__head">
            <div class="fg-card__icon"><Icon name="users" :size="16" /></div>
            <h2 class="fg-card__title">{{ t('summary.passengers') }}</h2>
            <span class="fg-card__count">{{ passengers.length }}</span>
            <button type="button" class="fg-card__edit" @click="goBack">{{ t('common.edit') }}</button>
          </header>
          <div class="fg-card__body">
            <template v-for="g in passengerGroups" :key="g.group">
              <div v-if="passengerGroups.length > 1" class="fg-group-head">
                {{ t('booking.passengersGroup', { group: g.group, count: g.passengers.length }) }}
              </div>
              <!-- Installation du groupe (fusionnée) : place aller / retour -->
              <div v-if="g.outwardPlace || g.returnPlace" class="fg-group-acc">
                <Icon name="seat" :size="12" class="fg-group-acc__icon" />
                <span v-if="g.outwardPlace" class="fg-group-acc__item">
                  <span v-if="returnAv" class="fg-group-acc__dir">{{ t('summary.outward') }}</span>{{ g.outwardPlace }}
                </span>
                <span v-if="g.returnPlace" class="fg-group-acc__item">
                  <span class="fg-group-acc__dir">{{ t('summary.return') }}</span>{{ g.returnPlace }}
                </span>
              </div>
              <div
                v-for="p in g.passengers"
                :key="p.order"
                class="fg-pax"
              >
                <div class="fg-pax__avatar">
                  <span>{{ initials(p) || p.order }}</span>
                </div>
                <div class="fg-pax__info">
                  <div class="fg-pax__name">{{ passengerDisplayName(p, p.order) }}</div>
                  <div class="fg-pax__meta">
                    <span class="fg-chip" :class="`fg-chip--${typeKey(p.type)}`">
                      {{ passengerTypeLabel(p.type) }}
                    </span>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </section>

        <!-- ══ 3. VÉHICULE (3 cols inline) ═══════════════════════ -->
        <section v-if="hasVehicle" class="fg-card">
          <header class="fg-card__head">
            <div class="fg-card__icon"><Icon name="car" :size="16" /></div>
            <h2 class="fg-card__title">{{ t('summary.vehicleDetails') }}</h2>
            <button type="button" class="fg-card__edit" @click="goBack">{{ t('common.edit') }}</button>
          </header>
          <div class="fg-card__body">
            <div class="fg-grid fg-grid--3">
              <div v-if="vehicleBrandModel || vehicleSize" class="fg-cell">
                <span class="fg-cell__label">{{ t('search.vehicleCard') }}</span>
                <span class="fg-cell__value fg-veh-ident">
                  <img v-if="vehicleLogo" :src="vehicleLogo" :alt="vehicleBrandModel" class="fg-veh-ident__logo" />
                  <span v-else-if="vehicleInitial" class="fg-veh-ident__badge" :style="vehicleBadgeStyle" aria-hidden="true">{{ vehicleInitial }}</span>
                  <span class="fg-veh-ident__main">
                    <span>{{ vehicleBrandModel || vehicleTypeLabel }}</span>
                    <span v-if="vehicleSize" class="fg-veh-ident__size">{{ vehicleSize }}</span>
                  </span>
                </span>
              </div>
              <div v-if="vehicleTypeLabel" class="fg-cell">
                <span class="fg-cell__label">{{ t('vehicle.step.type') }}</span>
                <span class="fg-cell__value">{{ vehicleTypeLabel }}</span>
              </div>
              <div v-if="vehiclePlate" class="fg-cell">
                <span class="fg-cell__label">{{ t('summary.vehiclePlate') }}</span>
                <span class="fg-cell__value fg-mono">{{ vehiclePlate }}</span>
              </div>
              <div v-if="store.search.vehicle?.priority" class="fg-cell">
                <span class="fg-cell__label">{{ t('vehicle.priority.title') }}</span>
                <span class="fg-cell__value">{{ t('vehicle.priority.priority') }}</span>
              </div>
            </div>
          </div>
        </section>

        <!-- ══ Animaux (optionnel, après véhicule) ═══════════════ -->
        <section v-if="hasPets" class="fg-card">
          <header class="fg-card__head">
            <div class="fg-card__icon"><Icon name="paw" :size="16" /></div>
            <h2 class="fg-card__title">{{ t('summary.petsDetails') }}</h2>
          </header>
          <div class="fg-card__body fg-card__rows">
            <div v-for="pet in petLines" :key="pet.key" class="fg-row">
              <span class="fg-row__label">{{ pet.label }}</span>
              <span class="fg-row__value fg-chip">×{{ pet.count }}</span>
            </div>
          </div>
        </section>

        <!-- ══ 4. CONTACT (3 cols inline) ═════════════════════════ -->
        <section class="fg-card">
          <header class="fg-card__head">
            <div class="fg-card__icon"><Icon name="user" :size="16" /></div>
            <h2 class="fg-card__title">{{ t('summary.contactDetails') }}</h2>
            <button type="button" class="fg-card__edit" @click="goBack">{{ t('common.edit') }}</button>
          </header>
          <div class="fg-card__body">
            <div class="fg-grid fg-grid--3">
              <div class="fg-cell">
                <span class="fg-cell__label">{{ t('summary.contactName') }}</span>
                <span class="fg-cell__value">
                  {{ store.search.contact.name || t('summary.notProvided') }}
                </span>
              </div>
              <div class="fg-cell">
                <span class="fg-cell__label">{{ t('summary.contactEmail') }}</span>
                <span class="fg-cell__value fg-mono">
                  {{ store.search.contact.email || t('summary.notProvided') }}
                </span>
              </div>
              <div class="fg-cell">
                <span class="fg-cell__label">{{ t('summary.contactPhone') }}</span>
                <span class="fg-cell__value fg-mono">
                  {{ phoneFull || t('summary.notProvided') }}
                </span>
              </div>
            </div>
          </div>
        </section>

        </div><!-- /.fg-confirm__main -->

        <!-- ══ 5. TARIFS + CTA ═══════════════════════════════════ -->
        <aside class="fg-confirm__aside">
        <section class="fg-card fg-card--checkout">
          <header class="fg-card__head">
            <div class="fg-card__icon"><Icon name="check" :size="16" /></div>
            <h2 class="fg-card__title">{{ t('summary.priceDetails') }}</h2>
          </header>
          <div class="fg-card__body">
            <div class="fg-price">
              <div v-if="outwardTariff" class="fg-price__row">
                <span class="fg-price__label">
                  {{ tariffLabel(outwardTariff.type) }}
                  <span class="fg-price__sub">· {{ t('summary.outward') }}</span>
                </span>
                <span class="fg-price__amount">
                  {{ formatPrice(outwardAmount, currencyOf(outwardTariff), locale) }}
                </span>
              </div>
              <!-- Sous-prix par groupe (dossier > 4 pax) — Aller. -->
              <div
                v-for="gp in (outwardGroupPrices.length > 1 ? outwardGroupPrices : [])"
                :key="`p-out-g${gp.group}`"
                class="fg-price__row fg-price__row--sub"
              >
                <span class="fg-price__label fg-price__sublabel">{{ t('summary.group', { group: gp.group }) }}</span>
                <span class="fg-price__amount">{{ formatPrice(amountOf(gp.tariff), currencyOf(gp.tariff), locale) }}</span>
              </div>
              <div v-if="returnTariff" class="fg-price__row">
                <span class="fg-price__label">
                  {{ tariffLabel(returnTariff.type) }}
                  <span class="fg-price__sub">· {{ t('summary.return') }}</span>
                </span>
                <span class="fg-price__amount">
                  {{ formatPrice(returnAmount, currencyOf(returnTariff), locale) }}
                </span>
              </div>
              <!-- Sous-prix par groupe (dossier > 4 pax) — Retour. -->
              <div
                v-for="gp in (returnGroupPrices.length > 1 ? returnGroupPrices : [])"
                :key="`p-ret-g${gp.group}`"
                class="fg-price__row fg-price__row--sub"
              >
                <span class="fg-price__label fg-price__sublabel">{{ t('summary.group', { group: gp.group }) }}</span>
                <span class="fg-price__amount">{{ formatPrice(amountOf(gp.tariff), currencyOf(gp.tariff), locale) }}</span>
              </div>
              <!-- Repas choisis (aller + retour) — même détail que l'étape Options. -->
              <div
                v-for="m in selectedMeals"
                :key="`p-meal-${m.key}`"
                class="fg-price__row"
              >
                <span class="fg-price__label">
                  {{ m.label }}
                  <span class="fg-price__sub">· ×{{ m.quantity }}</span>
                </span>
                <span class="fg-price__amount">{{ formatPrice(m.amount, m.currency, locale) }}</span>
              </div>
              <p class="fg-price__note">{{ t('summary.taxesIncluded') }}</p>
            </div>

            <div class="fg-total">
              <span class="fg-total__label">{{ t('summary.total') }}</span>
              <span class="fg-total__value">{{ totalFormatted }}</span>
            </div>

            <!-- Warning si redirection impossible -->
            <p
              v-if="!canRedirect"
              class="fg-checkout__warning"
              role="alert"
            >
              <Icon name="alert" :size="14" />
              <span v-if="store.mode === 'b2c'">
                {{ t('summary.checkoutUnavailable') }}
              </span>
              <span v-else>
                {{ t('summary.missingCheckoutUrl') }}
              </span>
            </p>

            <!-- CTA -->
            <button
              type="button"
              class="fg-cta"
              :class="{ 'fg-cta--disabled': !canRedirect || redirecting }"
              :disabled="!canRedirect || redirecting"
              @click="proceed"
            >
              <span v-if="redirecting" class="fg-cta__spinner" />
              <span class="fg-cta__label">
                {{ redirecting
                  ? t('summary.redirecting')
                  : (store.mode === 'b2c' ? t('summary.b2cCta') : t('summary.agentCta')) }}
              </span>
              <Icon v-if="!redirecting" name="arrow-right" :size="16" />
            </button>

            <button
              type="button"
              class="fg-checkout__newsearch"
              :disabled="redirecting"
              @click="newBooking"
            >
              {{ t('summary.newBooking') }}
            </button>
          </div>
        </section>
        </aside><!-- /.fg-confirm__aside -->
      </div>
    </template>
  </section>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { resetForNewSearch, useStore } from '../store'
import { useI18n } from '../i18n'
import { useRouter } from '../router'
import { useTracker } from '../tracker'
import { routeKey } from '../utils/track-meta'
import { BookingStep, PassengerType } from '../../../shared/types/enums'
import { activeGroups, computeBookingTotal, computeCrossingDuration, groupKey, passengersForGroup, passengersForPlace } from '../../../shared/rules'
import type { Availability } from '../../../shared/types'
import { formatDuration, formatPort, formatPrice, formatTime, humanizeLabel, normalizeBrandLogo } from '../utils/format'
import { useSelection } from '../composables/useSelection'
import BackButton from './atoms/BackButton.vue'
import Stepper from './atoms/Stepper.vue'
import Icon from './Icon.vue'

const store = useStore()
const router = useRouter()
const tracker = useTracker()
const { t, locale } = useI18n()

const redirecting = ref(false)

const bookingId = computed<string | null>(() => store.booking?.id ?? null)

// Sélection courante (av représentative + tarifs + places, groupes 1 & 2) —
// composable PARTAGÉ avec BookingSummary.
const {
  outwardAv, returnAv,
  outwardTariff, returnTariff, allTariffs, outwardAmount, returnAmount,
  outwardGroupPrices, returnGroupPrices,
  selectedMeals, mealsAmount,
  tariffLabel, amountOf, currencyOf,
} = useSelection()

// ─── Trajets ─────────────────────────────────────────────
// Durée : champ provider en PRIORITÉ (les ISO des providers portent des
// timezones « menteuses » → un diff Date.parse donnerait 44 h au lieu des
// 45 h réelles affichées à l'étape Traversée). Fallback : diff horaire.
const outwardDuration = computed<string>(() => durationFor(outwardAv.value))
const returnDuration = computed<string>(() => durationFor(returnAv.value))

function durationFor(av: Availability | null): string {
  if (!av) return ''
  return formatDuration(av.duration) || computeDuration(av.depart_at, av.arrive_at)
}

// ─── Passagers + installation (fusionnés par sous-groupe) ─
const passengers = computed(() => store.search.passengers)

function placeLabel(place: { name?: string; code?: string } | null | undefined): string {
  return place ? humanizeLabel(place.name || place.code || '') : ''
}

// Résumé final FUSIONNÉ : chaque sous-groupe (dispatch `in_board`) porte SES
// passagers ET son installation (place aller/retour). Fallback découpage par
// taille si pas d'allocation.
const passengerGroups = computed(() => {
  const dossier = store.search.passengers
  return activeGroups(dossier.length).map((group) => {
    const gk = groupKey(group)
    const place = store.selectedPlaces.outward[gk] ?? store.selectedPlaces.return[gk] ?? null
    return {
      group,
      passengers: passengersForPlace(place, dossier) ?? passengersForGroup(dossier, group),
      outwardPlace: placeLabel(store.selectedPlaces.outward[gk]),
      returnPlace: placeLabel(store.selectedPlaces.return[gk]),
    }
  })
})

// ─── Véhicule ────────────────────────────────────────────
const hasVehicle = computed<boolean>(() => {
  const v = store.search.vehicle
  return Boolean(v?.type && v.type !== '' && v.type !== 'NoCar')
})
const vehicleBrandModel = computed<string>(() => {
  const v = store.search.vehicle
  // /!\ v.brand contient l'UUID (alias legacy de brand_id). Le nom humain
  // est dans `brand_name`. Sinon on affiche "401ae831-... Twizy" au lieu de
  // "Renault Twizy".
  const parts = [v?.brand_name, v?.model].filter((s) => s && String(s).trim() !== '') as string[]
  return parts.join(' ')
})
const vehicleTypeLabel = computed<string>(() => {
  const type = store.search.vehicle?.type
  if (!type) return ''
  const key = `vehicle.type${capitalize(type)}`
  const translated = t(key)
  return translated === key ? type : translated
})
const vehiclePlate = computed<string>(() => {
  const id = store.search.vehicle?.identification
  return id ? String(id).trim() : ''
})
// Taille : length/height en mètres (le drawer les commit déjà via toFixed(2)).
const vehicleSize = computed<string>(() => {
  const v = store.search.vehicle
  const len = Number(v?.length)
  const h = Number(v?.height)
  const f = (n: number): string => `${n.toFixed(2)} m`
  if (len > 0 && h > 0) return `${f(len)} × ${f(h)}`
  if (len > 0) return f(len)
  return ''
})
// Logo de marque (peuplé par le drawer dès que le backend renvoie brand.logo).
// normalizeBrandLogo neutralise la transfo Cloudinary `g_face` qui casse l'image.
const vehicleLogo = computed<string>(() => normalizeBrandLogo(store.search.vehicle?.logo))
// Fallback sans logo : pastille « avatar » avec l'initiale de la marque
// (couleur stable dérivée du nom). Cède la place au vrai logo dès qu'il existe.
const vehicleInitial = computed<string>(() => {
  const src = store.search.vehicle?.brand_name || vehicleTypeLabel.value || ''
  const ch = src.trim().charAt(0)
  return ch ? ch.toUpperCase() : ''
})
const vehicleBadgeStyle = computed<{ background: string; color: string }>(() => {
  const name = store.search.vehicle?.brand_name || vehicleTypeLabel.value || ''
  const palette: ReadonlyArray<[string, string]> = [
    ['#EFF6FF', '#1D4ED8'], ['#F0FDF4', '#15803D'], ['#FEF2F2', '#B91C1C'],
    ['#FFF7ED', '#C2410C'], ['#FAF5FF', '#7E22CE'], ['#F0FDFA', '#0F766E'],
    ['#FEFCE8', '#A16207'], ['#FDF2F8', '#BE185D'],
  ]
  let h = 0
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0
  const [background, color] = palette[h % palette.length]
  return { background, color }
})

// ─── Animaux ─────────────────────────────────────────────
const hasPets = computed<boolean>(() => {
  const p = store.search.pets
  return (p?.cats || 0) + (p?.dogs || 0) + (p?.dogs30 || 0) > 0
})
const petLines = computed<Array<{ key: string; label: string; count: number }>>(() => {
  const p = store.search.pets || { cats: 0, dogs: 0, dogs30: 0 }
  const out: Array<{ key: string; label: string; count: number }> = []
  if (p.cats > 0) out.push({ key: 'cats', label: t('animals.cats'), count: p.cats })
  if (p.dogs > 0) out.push({ key: 'dogs', label: t('animals.dogs'), count: p.dogs })
  if (p.dogs30 > 0) out.push({ key: 'dogs30', label: t('animals.dogs30'), count: p.dogs30 })
  return out
})

// ─── Contact ─────────────────────────────────────────────
const phoneFull = computed<string>(() => {
  const c = store.search.contact
  const prefix = (c.prefix || '').trim()
  const phone = (c.phone || '').trim()
  if (!phone) return ''
  return prefix ? `${prefix} ${phone}` : phone
})

// ─── Tarifs ──────────────────────────────────────────────
// Total = tous les groupes (1 + 2) + repas choisis — pas de sous-facturation
// > 4 passagers, et cohérent avec le total affiché aux étapes Options/Booking.
const totalFormatted = computed(() => {
  const total = computeBookingTotal({ tariffs: allTariffs.value })
  return formatPrice(total.amount + mealsAmount.value, total.currency, locale.value)
})

// ─── Redirect ────────────────────────────────────────────
const canRedirect = computed<boolean>(() => {
  if (!bookingId.value) return false
  if (store.mode === 'b2c') return Boolean(store.checkoutUrl)
  return Boolean(store.saasBaseUrl)
})

function proceed(): void {
  if (!canRedirect.value || !bookingId.value) return
  redirecting.value = true
  const target = buildRedirectUrl()
  if (target === null) {
    redirecting.value = false
    return
  }
  // Jalon funnel : redirection vers le checkout marchand (paiement hors widget).
  tracker.emit('checkout_redirect', { bookingId: bookingId.value, route: routeKey(store.search.journeys) })
  try {
    if (window.top && window.top !== window) {
      window.top.location.href = target
    } else {
      window.location.href = target
    }
  } catch {
    window.location.href = target
  }
}

function buildRedirectUrl(): string | null {
  const id = bookingId.value
  if (!id) return null
  if (store.mode === 'b2c') {
    const base = store.checkoutUrl
    if (!base) return null
    // Le checkout de l'hôte charge le dossier BEIN → on passe l'id bein renvoyé
    // par actionSave. Fallback id FerryGate si bein non configuré.
    const checkoutId = store.beinBookingId ?? id
    const sep = base.includes('?') ? '&' : '?'
    // On passe la langue de l'utilisateur à la page de checkout de l'hôte :
    // l'URL de checkout n'a pas de préfixe de locale, donc l'hôte ne pourrait
    // pas la deviner autrement.
    const lang = encodeURIComponent(locale.value || 'fr')
    return `${base}${sep}booking_id=${encodeURIComponent(checkoutId)}&lang=${lang}`
  }
  const saas = (store.saasBaseUrl || '').replace(/\/$/, '')
  const lang = locale.value || 'fr'
  return `${saas}/${lang}/app/search/ferry/confirmation/${encodeURIComponent(id)}`
}

function newBooking(): void {
  resetForNewSearch(store)
  router.goTo(BookingStep.Search)
}
function goBack(): void { router.goTo(BookingStep.Booking) }

// ─── Helpers ─────────────────────────────────────────────
function typeKey(type: string): 'adult' | 'child' | 'baby' | 'other' {
  if (type === PassengerType.Adult) return 'adult'
  if (type === PassengerType.Child) return 'child'
  if (type === PassengerType.Baby) return 'baby'
  return 'other'
}
function passengerTypeLabel(type: string): string {
  if (type === PassengerType.Adult) return t('summary.adult')
  if (type === PassengerType.Child) return t('summary.child')
  if (type === PassengerType.Baby) return t('summary.baby')
  return type
}
interface PassengerNameSource {
  first_name?: string
  firstName?: string
  last_name?: string
  lastName?: string
}
function passengerDisplayName(p: PassengerNameSource | null | undefined, fallbackIndex: number): string {
  const first = (p?.first_name || p?.firstName || '').trim()
  const last = (p?.last_name || p?.lastName || '').trim()
  const full = [first, last].filter(Boolean).join(' ')
  if (full) return full
  return t('summary.passenger', { n: fallbackIndex })
}
function initials(p: PassengerNameSource | null | undefined): string {
  const first = (p?.first_name || p?.firstName || '').trim()
  const last = (p?.last_name || p?.lastName || '').trim()
  const a = first.charAt(0).toUpperCase()
  const b = last.charAt(0).toUpperCase()
  return (a + b) || ''
}
function formatDateHuman(raw: string | null | undefined): string {
  if (!raw) return ''
  try {
    const d = new Date(raw)
    if (!Number.isFinite(d.getTime())) return raw
    return d.toLocaleDateString(locale.value, { weekday: 'short', day: 'numeric', month: 'short' })
  } catch {
    return raw
  }
}
function computeDuration(depart?: string | null, arrive?: string | null): string {
  const hours = computeCrossingDuration(depart, arrive)
  return hours === null ? '' : formatDuration(Math.round(hours * 60))
}
function capitalize(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s
}
</script>
